import React from 'react'
import { Link } from 'react-router-dom'
import { CBLogoV2 } from '../../lib/components/Icon'

type VerifyEmailOtpPresenterProps = {
  email: string
  otpDigits: string[]
  countdown: number
  isSubmitting: boolean
  isResending: boolean
  onOtpChange: (index: number, value: string) => void
  onOtpKeyDown: (index: number, event: React.KeyboardEvent<HTMLInputElement>) => void
  onOtpPaste: (event: React.ClipboardEvent<HTMLDivElement>) => void
  onVerify: () => void
  onResend: () => void
  setInputRef: (index: number, element: HTMLInputElement | null) => void
  onOpenContactSupport: () => void
}

const VerifyEmailOtpPresenter = ({
  email,
  otpDigits,
  countdown,
  isSubmitting,
  isResending,
  onOtpChange,
  onOtpKeyDown,
  onOtpPaste,
  onVerify,
  onResend,
  setInputRef,
  onOpenContactSupport,
}: VerifyEmailOtpPresenterProps) => {
  const otpValue = otpDigits.join('')
  const canVerify = otpValue.length === 6 && !isSubmitting
  const canResend = countdown === 0 && !isResending

  return (
    <div className='verify-account-page'>
      <div className='verify-account-page__panel'>
        <div className='verify-account-page__content'>
          <div className='verify-account-page__logo'>
            <CBLogoV2 classes={['verify-account-page__logo-svg']} width='154' height='36' />
          </div>
          <div className='verify-account-page__card'>
            <h1 className='verify-account-page__title'>Verify your email</h1>
            <p className='verify-account-page__subtitle'>
              We&apos;ve sent a 6-digit code to <strong>{email}</strong>
            </p>

            <div className='verify-account-page__otp' onPaste={onOtpPaste}>
              {otpDigits.map((digit, index) => (
                <input
                  key={`otp-${index}`}
                  ref={(element) => setInputRef(index, element)}
                  className='verify-account-page__otp-input'
                  inputMode='numeric'
                  type='text'
                  maxLength={1}
                  value={digit}
                  onChange={(event) => onOtpChange(index, event.target.value)}
                  onKeyDown={(event) => onOtpKeyDown(index, event)}
                  aria-label={`OTP digit ${index + 1}`}
                />
              ))}
            </div>

            <button
              type='button'
              className='verify-account-page__verify-btn'
              disabled={!canVerify}
              onClick={onVerify}
            >
              {isSubmitting ? 'Verifying...' : 'Verify Account'}
            </button>

            <div className='verify-account-page__resend-block'>
              <div className='verify-account-page__timer'>
                {countdown > 0 ? `00:${String(countdown).padStart(2, '0')}` : '00:00'}
              </div>
              <button
                type='button'
                className='verify-account-page__resend-btn'
                disabled={!canResend}
                onClick={onResend}
              >
                {isResending ? 'Sending...' : 'Resend Code'}
              </button>
            </div>

            <div className='verify-account-page__footer-note'>
              This verification helps protect your account from unauthorized access.
            </div>
          </div>
          <div className='verify-account-page__support'>
            Need help?{' '}
            <button
              type='button'
              className='verify-account-page__support-link'
              onClick={onOpenContactSupport}
            >
              Contact support
            </button>
            <Link to='/login' className='verify-account-page__skip-link'>
              Skip setup for now
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export { VerifyEmailOtpPresenter }
